import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import {Helmet} from '@dr.pogodin/react-helmet';
import {isFeatureEnabled} from 'feature-flags';
import {DataStatus} from 'platform/components';
import {Box, HStack, Show, VStack} from 'platform/foundation';

import {ReactNode, useRef} from 'react';

import {isEmpty, isNil, isNotNil, isNotEmpty} from 'ramda';

import {
  SourcingUserListQuery,
  useAuctionReorderUserComparisonMutation,
  useSourcingReorderUserComparisonMutation,
} from '@dms/api/core';
import {useGetUserStatsQuery} from '@dms/api/customer';
import {featureFlags} from '@dms/feature-flags';
import {i18n} from '@dms/i18n';
import {COUNTRY_CZE, formatDocumentTitle, handleApiError} from '@dms/shared';

import {pushGtmEvent, suffixTestId, TestIdProps} from 'shared/web';

import {InfiniteScrollFooterStatus} from '../../../components/InfiniteScrollListener/components/InfiniteScrollFooterStatus';
import {InfiniteScrollListener} from '../../../components/InfiniteScrollListener/InfiniteScrollListener';
import {SourcingDataStatus} from '../../../components/SourcingDataStatus/SourcingDataStatus';
import {DEFAULT_CURRENCY_CODE} from '../../../constants/DEFAULT_CURRENCY_CODE';
import {useAuctionEnumLookup} from '../../../hooks/useAuctionEnumLookup';
import {useExportSourcingUserList} from '../../../hooks/useExportSourcingUserList';
import {usePaginatedUserList} from '../../../hooks/usePaginatedUserList';
import {useSourcingCurrency} from '../../../hooks/useSourcingCurrency';
import {useSourcingListAvailability} from '../../../hooks/useSourcingListAvailability';
import {useSourcingPricingStrategy} from '../../../hooks/useSourcingPricingStrategy';
import {useSourcingTargetCountry} from '../../../hooks/useSourcingTargetCountry';
import {SourcingType, useSourcingType} from '../../../hooks/useSourcingType';
import {useSourcingUserListCount} from '../../../hooks/useSourcingUserListCount';
import {SourcingPage} from '../../../types/SourcingPage';
import {getSourcingUserListByPage} from '../../../utils/getSourcingUserListByPage';
import {isUnifiedSourcingEnabled} from '../../../utils/isUnifiedSourcingEnabled';
import {useClearComparisonList} from '../hooks/useClearComparisonList';
import {getMinMaxValues} from '../utils/getMinMaxValues';
import {isComparisonVehicleInactive} from '../utils/isComparisonVehicleInactive';
import {sortAndUpdateVehicleFeatures} from '../utils/sortAndUpdateVehicleFeatures';
import {ComparisonHoverSurface} from './ComparisonHoverSurface';
import {ComparisonPageHeader} from './ComparisonPageHeader';
import {ComparisonSkeleton} from './ComparisonSkeleton';
import {LabelColumn} from './LabelColumn';
import {SortableVehicleColumn} from './SortableVehicleColumn';

const LOAD_MORE_SCROLL_THRESHOLD_PX = 80;

/** Taken from the shared page mapping, so this page and its bulk action drawer export the same list. */
const USER_LIST = getSourcingUserListByPage(SourcingPage.COMPARISON_PAGE);

export function UserComparisonPage(props: TestIdProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasUnifiedSurfaces = isUnifiedSourcingEnabled();
  const {data: userStats, isLoading: isUserStatsLoading} = useGetUserStatsQuery();
  const comparisonCount = userStats?.userStats.comparisonCount;
  const currency = useSourcingCurrency();
  const targetCountry = useSourcingTargetCountry();
  const pricingStrategy = useSourcingPricingStrategy();
  const sourcingType = useSourcingType();
  const enumLookup = useAuctionEnumLookup();
  const [reorderClassifiedsVehicles] = useSourcingReorderUserComparisonMutation();
  const [reorderAuctionVehicles] = useAuctionReorderUserComparisonMutation();
  const exportCount = useSourcingUserListCount(USER_LIST);
  // No sort: the backend exports the comparison in the order the user arranged it.
  const {exportUserList, isExporting} = useExportSourcingUserList(USER_LIST);
  const handleExport = () => exportUserList();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const query: SourcingUserListQuery = {
    list: 'user-comparison',
    currency: currency?.code ?? DEFAULT_CURRENCY_CODE,
    buyerCountry: targetCountry?.code ?? COUNTRY_CZE,
    pricingStrategy,
  };
  const {queryResult, fulfilledTimeStamp, auctionVehicles, isTerminalEmpty} = usePaginatedUserList({
    query,
    sourcingType,
    enumLookup,
  });
  const {clear: handleDelete, isDeleting} = useClearComparisonList(sourcingType);
  const vehicles = queryResult.data;
  const isLoading = queryResult.isLoading || (hasUnifiedSurfaces && isUserStatsLoading);
  const {isFetching, isEndReached, error} = queryResult;
  const vehicleIds = vehicles.map((vehicle) => vehicle.sourcingVehicle.id);
  // Comparison highlights are relative to the columns the user has loaded so far.
  const vehiclesWithFeatures = sortAndUpdateVehicleFeatures(vehicles);
  const minMaxValues = getMinMaxValues(vehicles);

  const classifiedsAdIds =
    sourcingType === SourcingType.CLASSIFIEDS
      ? vehicles.map((vehicle) => vehicle.sourcingVehicle.adId)
      : [];
  useSourcingListAvailability({
    adIds: classifiedsAdIds,
    enabled:
      isFeatureEnabled(featureFlags.SOURCING_AVAILABILITY_CHECK) &&
      sourcingType === SourcingType.CLASSIFIEDS,
    userLoadKey: fulfilledTimeStamp ?? 0,
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const {active, over} = event;

    if (!over || active.id === over.id || isFetching || isDeleting || !isEndReached) {
      return;
    }

    const oldIndex = vehicles.findIndex((vehicle) => vehicle.sourcingVehicle.id === active.id);
    const newIndex = vehicles.findIndex((vehicle) => vehicle.sourcingVehicle.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      if (sourcingType === SourcingType.CLASSIFIEDS) {
        const reorderedVehicles = arrayMove(vehicles, oldIndex, newIndex);
        reorderClassifiedsVehicles(reorderedVehicles)
          .unwrap()
          .then(() => {
            pushGtmEvent({
              event: `sourcing_${sourcingType}_comparison_sorted`,
              event_id: `sourcing_${sourcingType}_comparison_sorted`,
            });
          })
          .catch(handleApiError);
      }

      if (sourcingType === SourcingType.AUCTIONS) {
        const reorderedAuctionVehicles = arrayMove(auctionVehicles, oldIndex, newIndex);
        reorderAuctionVehicles(reorderedAuctionVehicles)
          .unwrap()
          .then(() => {
            pushGtmEvent({
              event: `sourcing_${sourcingType}_comparison_sorted`,
              event_id: `sourcing_${sourcingType}_comparison_sorted`,
            });
          })
          .catch(handleApiError);
      }
    }
  };

  return (
    <>
      <Helmet
        title={formatDocumentTitle(
          i18n.t('general.actions.comparison'),
          sourcingType === SourcingType.CLASSIFIEDS
            ? i18n.t('page.sourcing.labels.classifieds')
            : i18n.t('page.sourcing.labels.auctions'),
          i18n.t('page.sourcing.labels.sourcing')
        )}
      />
      <VStack height="100%">
        <ComparisonPageHeader
          isLoading={isLoading}
          isDeleting={isDeleting}
          hasVehicles={isLoading || isNotEmpty(vehicles) || (queryResult.count ?? 0) > 0}
          onDelete={handleDelete}
          vehiclesCount={exportCount}
          isExporting={isExporting}
          onExport={handleExport}
          data-testid={suffixTestId('comparisonPageHeader', props)}
        />
        <VStack grow={1} minHeight={0} wrap="nowrap">
          <SourcingDataStatus
            isEmpty={isTerminalEmpty}
            iconUrl="/assets/images/empty_images/empty-compare.svg"
            text={i18n.t('entity.vehicle.notifications.nothingInComparison')}
          >
            <ComparisonLoadingBoundary
              isLoading={isLoading}
              comparisonCount={comparisonCount}
              hasUnifiedSurfaces={hasUnifiedSurfaces}
            >
              <DataStatus
                isLoading={queryResult.isLoading}
                isError={isNotNil(queryResult.error) && isEmpty(vehicles)}
                minHeight="100%"
                data-testid={suffixTestId('comparisonDataStatus', props)}
                errorAction={{
                  title: i18n.t('general.actions.retry'),
                  variant: 'secondary',
                  onClick: queryResult.onRetry,
                  isLoading: queryResult.isFetching,
                }}
              >
                <Box
                  ref={scrollContainerRef}
                  width="100%"
                  flex={1}
                  minHeight={0}
                  overflow="auto"
                  position="relative"
                  data-testid={suffixTestId('comparisonScrollContainer', props)}
                >
                  <InfiniteScrollListener
                    scrollContainerRef={scrollContainerRef}
                    isFetching={isFetching}
                    isEnabled={!isDeleting && !isEndReached && isNil(error)}
                    onLoadMore={queryResult.onLoadMore}
                    axis="horizontal"
                    thresholdPx={LOAD_MORE_SCROLL_THRESHOLD_PX}
                  />
                  <ComparisonHoverSurface
                    hasUnifiedSurfaces={hasUnifiedSurfaces}
                    data-testid={props['data-testid']}
                  >
                    <LabelColumn />
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext items={vehicleIds} strategy={horizontalListSortingStrategy}>
                        {vehiclesWithFeatures.map((vehicle) => (
                          <SortableVehicleColumn
                            key={vehicle.sourcingVehicle.id}
                            vehicle={vehicle}
                            minMaxValues={minMaxValues}
                            isInactiveAd={isComparisonVehicleInactive(
                              sourcingType,
                              vehicle.sourcingVehicle
                            )}
                            isDragDisabled={isFetching || isDeleting || !isEndReached}
                            data-testid={suffixTestId('vehicleColumn', props)}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                    <Show
                      when={
                        isNotEmpty(vehicles) &&
                        (queryResult.isFetchingNextPage || isEndReached || isNotNil(error))
                      }
                    >
                      <Box
                        role="status"
                        width={40}
                        flexShrink={0}
                        paddingHorizontal={4}
                        data-testid={suffixTestId('loadStatus', props)}
                      >
                        <Box position="sticky" top="50%">
                          <HStack justify="center">
                            <InfiniteScrollFooterStatus
                              showRetry={isNotNil(error)}
                              showSpinner={queryResult.isFetchingNextPage && isNil(error)}
                              showComplete={
                                isEndReached && isNil(error) && !queryResult.isFetchingNextPage
                              }
                              isFetching={isFetching}
                              onRetry={queryResult.onRetry}
                              completeText={i18n.t('entity.sourcingTable.labels.allResultsLoaded')}
                              data-testid={props['data-testid']}
                            />
                          </HStack>
                        </Box>
                      </Box>
                    </Show>
                  </ComparisonHoverSurface>
                </Box>
              </DataStatus>
            </ComparisonLoadingBoundary>
          </SourcingDataStatus>
        </VStack>
      </VStack>
    </>
  );
}

interface ComparisonLoadingBoundaryProps {
  children: ReactNode;
  isLoading: boolean;
  comparisonCount?: number;
  hasUnifiedSurfaces: boolean;
}

function ComparisonLoadingBoundary(props: ComparisonLoadingBoundaryProps) {
  return props.hasUnifiedSurfaces && props.isLoading ? (
    <ComparisonSkeleton vehicleCount={props.comparisonCount} />
  ) : (
    <>{props.children}</>
  );
}
